/* eslint-disable max-lines */
import React, { useMemo } from 'react';
import loadable from '@loadable/component';
import styled from 'styled-components';
import useConfig from 'AppShell/hooks/useConfig';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { ClubAppLinks, FooterLinks } from 'OneWestwingFooter/types';
import BellaHybridLink from 'Client/components/Generic/Links/BellaHybridLink';
import useOwwFooterTracking, {
    FOOTER_TRACKING_EVENT_CATEGORY,
} from 'OneWestwingFooter/providers/OwwFooterTrackingProvider';
import { getFooterAppURL } from '../helpers/getFooterAppURL';
import { DeviceType, DeviceTypes } from 'AppShell/appshell_types/appshellMetaTypes';
import { ClubAdjustTrackers } from 'AppShell/appshell_types/clubHelpers';
import useDeviceType from 'Client/hooks/useDeviceType';
import useAppshellHistoryPush from 'AppShell/hooks/useAppshellHistoryPush';
import { useFooterPartnersInfo } from 'OneWestwingFooter/hooks/useFooterPartnersInfo';
import { SocialMediaData } from 'OneWestwingFooter/hooks/useFooterSocialMedia';
import { getClubAppLink } from 'Club/utils/getClubAppLink';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

const MobileAppLogo = loadable(
    (props: { width?: number; height?: number; path: string }) =>
        import(`@westwing/ui-kit/Icons/MobileApps/${props.path}/index.js`)
);

enum MobileAppCountries {
    CZ = 'Cz',
    DE = 'De',
    PL = 'Pl',
    IT = 'It',
    SK = 'Sk',
    FR = 'Fr',
    NL = 'Nl',
    ES = 'Es',
    PT = 'Pt',
}

const SocialMediaWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
`;

const SocialMediaLink = styled(BellaHybridLink)<{ $noMargin?: boolean; $height: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: ${({ $height }) => $height}px;
    color: ${({ theme }) => theme.colors.charcoal100};
    margin-right: ${({ $noMargin }) => ($noMargin ? '0' : '32px')};
    box-sizing: border-box;
`;

const IconWrapper = styled.div`
    line-height: 0;

    > svg {
        background-color: ${({ theme }) => theme.colors.white};
        outline: ${({ theme }) => `1px solid ${theme.colors.charcoal20}`};
        box-sizing: border-box;
    }
`;

const MobileAppLogoWrapper = styled.div`
    line-height: 0;
    outline: 0;
    cursor: pointer;
`;

const SocialMedia = ({ socialMedia }: { socialMedia: SocialMediaData[] }) => {
    const { ga } = useOwwFooterTracking();

    const handleClick = (trackingAction: string, platform: string) => {
        if (trackingAction && platform) {
            ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
                action: trackingAction,
                label: platform,
            });
        }
    };

    return (
        <SocialMediaWrapper>
            {socialMedia.map((media, index) => (
                <SocialMediaLink
                    title={media.platform}
                    onClick={() => handleClick('Social Media icon - click', media.platform)}
                    to={media.link}
                    $height={media.height ?? 32}
                    $noMargin={index === socialMedia.length - 1}
                    key={`${`Footer_SocialMedia_${index}`}`}
                >
                    <media.Logo />
                </SocialMediaLink>
            ))}
        </SocialMediaWrapper>
    );
};

const getLogoCountry = (currentCountry: string) => {
    const country = `${currentCountry?.charAt(0).toUpperCase()}${currentCountry?.slice(1)}`;

    if (Object.values(MobileAppCountries).includes(country as MobileAppCountries)) {
        return country;
    }

    return MobileAppCountries.DE;
};

const MobileApp = ({
    appFor,
    currentCountry,
    clubAliceLink,
    isClub,
}: {
    appFor: DeviceType;
    currentCountry: string;
    clubAliceLink?: string;
    isClub: boolean;
}) => {
    const { p100ChangeSmartAppBannerLink } = useFeatureToggle();
    const { ga } = useOwwFooterTracking();
    const { getByUserAgent } = useDeviceType();
    const appshellHistoryPush = useAppshellHistoryPush(() => true);
    const { links } = useConfig();

    const handleClick = () => {
        ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
            action: 'App icon - click',
            label: appFor === DeviceTypes.ANDROID ? 'Android' : 'iOS',
        });

        if (isClub) {
            const appData = { host: links.clubBaseUrl };

            // eslint-disable-next-line prettier/prettier
            appshellHistoryPush(clubAliceLink ?? getClubAppLink(getByUserAgent(), appFor, currentCountry, ClubAdjustTrackers.FOOTER, appData));
        } else {
            appshellHistoryPush(
                getFooterAppURL(getByUserAgent(), appFor, currentCountry, p100ChangeSmartAppBannerLink)
            );
        }
    };

    return (
        <MobileAppLogoWrapper aria-label={appFor} role="link" tabIndex={0} onKeyUp={handleClick} onClick={handleClick}>
            <MobileAppLogo
                height={32}
                path={`${appFor === DeviceTypes.ANDROID ? 'GooglePlay' : 'AppStore'}/${getLogoCountry(currentCountry)}`}
            />
        </MobileAppLogoWrapper>
    );
};

const useFooterPartners = (isClub: boolean, clubAppLinks?: ClubAppLinks) => {
    const { currentCountry } = useConfig();
    const t = useBellaTranslator();

    const { paymentProviders, deliveryPartners, socialMedia } = useFooterPartnersInfo(isClub);

    const clubIosAppLink = isClub ? clubAppLinks?.ios : undefined;
    const clubAndroidAppLink = isClub ? clubAppLinks?.android : undefined;

    const partnersLinks: FooterLinks[] = useMemo(
        () => [
            {
                title: t('Footer_Payment_provider'),
                key: 'footer_title_Payment_provider',
                accordionTrackingActions: {
                    open: 'Payment provider - open',
                    close: 'Payment provider - close',
                },
                items: paymentProviders.map(Provider => ({
                    content: (
                        <IconWrapper>
                            <Provider width={48} height={32} />
                        </IconWrapper>
                    ),
                })),
            },
            {
                title: t('Footer_Delivery_partner'),
                key: 'footer_title_Delivery_partner',
                accordionTrackingActions: {
                    open: 'Delivery partner - open',
                    close: 'Delivery partner - close',
                },
                items: deliveryPartners.map(Partner => ({
                    content: (
                        <IconWrapper>
                            <Partner width={48} height={32} />
                        </IconWrapper>
                    ),
                })),
            },
            {
                title: t('Footer_Apps'),
                showTitleOnMobile: true,
                key: 'footer_title_Apps',
                items: [
                    {
                        content: (
                            <MobileApp
                                appFor={DeviceTypes.ANDROID}
                                currentCountry={currentCountry}
                                clubAliceLink={clubAndroidAppLink}
                                isClub={isClub}
                            />
                        ),
                    },
                    {
                        content: (
                            <MobileApp
                                appFor={DeviceTypes.IOS}
                                currentCountry={currentCountry}
                                clubAliceLink={clubIosAppLink}
                                isClub={isClub}
                            />
                        ),
                    },
                ],
            },
            {
                title: t('Footer_Social_Media'),
                key: 'footer_title_Social_Media',
                items: [
                    {
                        content: socialMedia.length ? <SocialMedia socialMedia={socialMedia} /> : null,
                    },
                ],
            },
        ],
        [currentCountry, isClub, clubAndroidAppLink, clubIosAppLink, deliveryPartners, paymentProviders, socialMedia, t]
    );

    return partnersLinks;
};

export default useFooterPartners;
